import React from 'react'
import Tippy from '@tippyjs/react'
import { observer } from 'mobx-react'
import { DashboardPositionStore } from 'stores/dashboard/dashboard-position'
import {
  DashboardPositionFormAlertText,
  DashboardPositionFormContentCard,
  DashboardPositionFormSection,
  DashboardPositionFormSectionTitle,
} from 'containers/dashboard/positions/styles'
import { Column, Row } from 'components/common/layout'
import { SemiBoldText } from 'components/common/text'
import { theme } from 'infra/theme'
import { Balloon, BalloonType } from 'components/common/balloon'
import { TooltipIcon } from 'components/common/icon'
import { DashboardPositionFormSlider } from 'containers/dashboard/positions/position-form-slider'
import {
  DASHBOARD_POSITION_FORM_SALARY_SECTION_ID,
  DEFAULT_MAX_SALARY,
} from 'infra/constants'
import { useIsDesktopCSR } from 'utils/hooks'
import { PositionFormSubInfoTitleText } from 'components/dashboard/positions/styles'
import { CheckboxChip, ChipSize, RadioChip } from 'components/common/chip'
import { StartupPositionStockOptionType, StockOptionEnum } from 'types/common'

const STOCK_OPTION_TYPE: StartupPositionStockOptionType[] = [
  'OFFERED',
  'NOT_OFFERED',
  'NEGOTIABLE',
]

export const DashboardPositionFormSalary: React.FC<{
  store: DashboardPositionStore
}> = observer(({ store }) => {
  const isDesktop = useIsDesktopCSR()
  return (
    <DashboardPositionFormSection
      id={DASHBOARD_POSITION_FORM_SALARY_SECTION_ID}
    >
      <Row style={{ gap: 16 }}>
        <DashboardPositionFormSectionTitle>
          예상 연봉
        </DashboardPositionFormSectionTitle>
        <Row style={{ gap: 2 }}>
          <SemiBoldText style={{ color: theme.color.gray2 }}>
            왜 필요한가요
          </SemiBoldText>
          <Tippy
            content={
              <Balloon variant={BalloonType.LOW_BLACK}>
                <ul style={{ paddingLeft: 12, textAlign: 'start' }}>
                  <li>
                    협의가 가능한 수준의 인재 위주로 지원자와 매니저 추천 인재를
                    받아보세요. 처우 협의 단계에서 채용이 결렬되는 비효율을 줄일
                    수 있어요.
                  </li>
                  <li>
                    설정하신 내용은 인재에게 절대 노출되지 않으니 걱정마세요
                  </li>
                </ul>
              </Balloon>
            }
          >
            <Row>
              <TooltipIcon size={16} />
            </Row>
          </Tippy>
        </Row>
      </Row>
      <DashboardPositionFormContentCard>
        <Column style={{ gap: 16 }}>
          {store.hasSalaryUnknownOption && (
            <CheckboxChip
              isSelected={store.isSalaryUnknown}
              onClick={() => {
                store.setIsSalaryUnknown(!store.isSalaryUnknown)
                store.setMinDesiredSalary(0)
                store.setMaxDesiredSalary(0)
                store.setMaxNegotiableSalary(0)
              }}
              size={ChipSize.SMALL}
            >
              예상 연봉을 알 수 없어요
            </CheckboxChip>
          )}
          {!store.isSalaryUnknown && (
            <>
              <Column style={{ gap: 8 }}>
                <DashboardPositionFormSlider
                  min={0}
                  step={100}
                  max={DEFAULT_MAX_SALARY}
                  description='희망하는 수준'
                  value={[
                    store.position.minDesiredSalary,
                    store.position.maxDesiredSalary,
                  ]}
                  unit='만원'
                  onChange={(v) => {
                    const _v = v as number[]
                    if (store.position.maxDesiredSalary !== _v[1])
                      store.setMaxNegotiableSalary(_v[1])
                    store.setMinDesiredSalary(_v[0])
                    store.setMaxDesiredSalary(_v[1])
                    if (_v[1] > store.position.maxNegotiableSalary) {
                      store.setMaxNegotiableSalary(_v[1])
                    }
                    store.onDataChanged()
                  }}
                />
                {store.position.maxDesiredSalary -
                  store.position.minDesiredSalary <=
                  500 && (
                  <DashboardPositionFormAlertText>
                    * 범위를 좁게 설정하면 다양한 인재를 추천해드릴 수 없어요
                  </DashboardPositionFormAlertText>
                )}
              </Column>
              <DashboardPositionFormSlider
                min={0}
                step={100}
                max={DEFAULT_MAX_SALARY}
                description={
                  isDesktop
                    ? '정말 뛰어난 인재를 만났을 때, 협상 가능한 최대 수준'
                    : '협상 가능한 최대 수준'
                }
                value={store.position.maxNegotiableSalary}
                unit='만원'
                onChange={(v) => {
                  const _v = v as number
                  store.setMaxNegotiableSalary(_v)
                  if (
                    _v < store.position.maxDesiredSalary &&
                    store.position.minDesiredSalary !==
                      store.position.maxDesiredSalary
                  ) {
                    store.setMaxDesiredSalary(_v)
                  }
                  if (
                    store.position.minDesiredSalary ===
                    store.position.maxDesiredSalary
                  ) {
                    store.setMinDesiredSalary(_v)
                    store.setMaxDesiredSalary(_v)
                  }
                  store.onDataChanged()
                }}
              />
            </>
          )}
        </Column>
        <Column style={{ gap: 8 }}>
          <PositionFormSubInfoTitleText>
            스톡옵션을 제공하나요?
          </PositionFormSubInfoTitleText>
          {STOCK_OPTION_TYPE.map((item) => (
            <RadioChip
              size={ChipSize.SMALL}
              isSelected={store.position.stockOption === item}
              key={item}
              onClick={() => {
                store.setStockOption(item)
                store.onDataChanged()
              }}
            >
              {StockOptionEnum[item]}
            </RadioChip>
          ))}
        </Column>
      </DashboardPositionFormContentCard>
    </DashboardPositionFormSection>
  )
})
